/**
 * Раздел «Исполнительные листы» (ИЛ-1b + УИ-2d), маршрут /projects/sheets.
 * Самостоятельный рабочий экран на таблице ccpm.project_sheets (общей с 1С).
 *
 * Список листов как в 1С: колонки Сделка / Наименование сокр. / № проекта / № ИП /
 * № договора / ГИП / Статус / Акт; фильтр «кроме закрытых» (актуальность), фильтр по
 * статусу, поиск, сортировка. «Создать». Ведёт себя как «Аукционы»: одинарный клик по
 * строке открывает карточку ИЛ в правой панели; контактные лица листа — внутри карточки
 * (пункт «Исполнительный лист» → вкладка «Контактное лицо»).
 *
 * УИ-2d: карточка листа переехала с полностраничной «как в 1С» на правую панель записи
 * (components/record-panel.js, одиночный render-body: сама рисует тело, футер = «Закрыть»).
 * Панель открывается развёрнутой (ti-maximized, ключ `sheets`), внутри тела — то же
 * вертикальное левое меню `.sheet-menu` (13 пунктов: Исполнительный лист,
 * Изменения → разрешения, Входящие письма → письма экспертизы, Замечания листа,
 * Нормоконтроль, Паспорт проекта, остальное — заглушки «будет») + область контента.
 * «Исполнительный лист» = шапка со всеми полями 1С и «Сохранить» + вкладки «Состав
 * проекта» / «График работ» (заглушка) / «Календарный план по договору» / «Контактное
 * лицо» (CRUD через openModal ПОВЕРХ панели). Правка — expertise.write, остальное —
 * чтение. Deep-link `?id=` открывает панель сразу; закрытие — url /projects/sheets.
 *
 * Данные: /api/expertise (backend/app/api/expertise.py). Сортировка — на клиенте
 * (у /sheets нет серверного sort; листов немного, бэкенд не трогаем).
 */
import { api } from '../api.js';
import { canDo } from '../auth.js';
import { createDataTable } from '../components/data-table.js';
import { openModal } from '../components/modal.js';
import { showNotification } from '../components/notification.js';
import { pickCounterparty, pickPerson } from '../components/ref-picker.js';
import { mountRecordPanel, openRecordPanel, closeRecordPanel } from '../components/record-panel.js';
import {
  DEFAULT_STATUS, PD_STAGES, SHEET_STATUSES, WORK_KINDS,
  actualLabel, filterByStatus, fmtDate, fmtDateInput, fmtMoney, sheetTitle, sortSheets, yesNo,
} from '../utils/sheets-format.js';
import { createChecksTable, renderPassport } from './normcheck.js';

const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const apiErrorText = (e) => e?.detail ?? e?.message ?? 'Ошибка';
const clip = (s) => `<span class="dt-clip" title="${esc(s ?? '')}">${esc(s || '—')}</span>`;

const PARSE_STATUS = {
  queued: 'ждёт ИИ-разбора',
  parsing: 'разбирается…',
  done: 'разобрано',
  failed: 'не разобрано',
};

const CHANGE_CODE_LABEL = {
  error_fix: 'Устранение ошибок',
  extra_requirements: 'Доп. требования',
  other: 'Другое',
};

// Левое меню карточки как в 1С. Рабочие пункты — первыми, заглушки — следом.
const CARD_NAV = [
  { key: 'card', label: 'Исполнительный лист' },
  { key: 'changes', label: 'Изменения' },
  { key: 'letters-in', label: 'Входящие письма' },
  { key: 'remarks', label: 'Замечания' },
  { key: 'normcheck', label: 'Нормоконтроль' },
  { key: 'passport', label: 'Паспорт проекта' },
  { key: 'ird', label: 'ИРД', stub: true },
  { key: 'letters-out', label: 'Исходящие письма', stub: true },
  { key: 'exp-contracts', label: 'Договора экспертиз', stub: true },
  { key: 'events', label: 'События', stub: true },
  { key: 'responsibles', label: 'Ответственные лица', stub: true },
  { key: 'orders', label: 'Приказы ОД', stub: true },
  { key: 'files', label: 'Присоединённые файлы', stub: true },
];

// Вкладки документа (внутри пункта «Исполнительный лист»).
const CARD_TABS = [
  { key: 'composition', label: 'Состав проекта' },
  { key: 'gantt', label: 'График работ', stub: true },
  { key: 'plan', label: 'Календарный план по договору' },
  { key: 'contacts', label: 'Контактное лицо' },
];

let _root = null;
let _table = null;
let _sections = null;
let _sheet = null;          // текущий лист в панели
let _activeTab = 'composition';
let _activeNav = 'card';     // активный пункт левого меню карточки
let _panelCtx = null;       // ctx render-body текущей панели (setRecord/rerender)
let _creating = false;      // панель открыта в режиме создания листа

export async function initSheets(container) {
  _table = null;
  _sheet = null;
  _panelCtx = null;
  _creating = false;
  container.innerHTML = `
    <div class="sheets-view tenders-view">
      <header class="tenders-header">
        <h2>Исполнительные листы</h2>
        <span class="tenders-hint">Проекты из 1С «ПКБ ТИТАН(Б)». Клик по строке — открыть карточку.</span>
      </header>
      <div class="sheets-body tenders-body"></div>
    </div>`;
  _root = container.querySelector('.sheets-view');
  // Открытая правая панель записи (УИ-2d) сжимает таблицу, а не закрывает её
  // (переиспользуем --tdi-space из tenders.css, как counterparties.js/expertise.js).
  _root.addEventListener('ccpm:inspector-resize', (e) => {
    _root.style.setProperty('--tdi-space', `${Math.round(e.detail.width)}px`);
  });
  // Богатая карточка «как в 1С» (шапка-форма + левое меню) читаема только во всю
  // ширину — монтируем панель развёрнутой (ti-maximized), пользователь может свернуть.
  mountRecordPanel(_root, { key: 'sheets', defaultWidth: 900, maximized: true });
  renderList();
  const id = Number(new URLSearchParams(location.search).get('id'));
  if (id) openSheetPanel(id);
}

async function loadSections() {
  if (_sections) return _sections;
  try { _sections = await api.expertise.sections(); } catch { _sections = []; }
  return _sections;
}

// ── Список листов ──────────────────────────────────────────────────────────────

const SHEET_COLUMNS = [
  { key: 'deal', label: 'Сделка', width: 150, sortKey: 'deal', render: s => clip(s.deal) },
  { key: 'short_name', label: 'Наименование сокр.', width: 280, sortKey: 'short_name', render: s => clip(s.short_name) },
  { key: 'project_no', label: 'Номер проекта', width: 120, sortKey: 'project_no', render: s => esc(s.project_no ?? '—') },
  { key: 'ip_no', label: 'Номер ИП', width: 110, sortKey: 'ip_no', render: s => esc(s.ip_no ?? '—') },
  { key: 'contract_no', label: '№ договора', width: 150, sortKey: 'contract_no', render: s => clip(s.contract_no) },
  { key: 'gip', label: 'ГИП', width: 170, sortKey: 'gip', render: s => clip(s.gip_user_name || s.gip) },
  { key: 'status', label: 'Статус', width: 130, sortKey: 'status', render: s => esc(s.status ?? '—') },
  { key: 'act', label: 'Акт', width: 150, sortKey: 'act', render: s => clip(s.act) },
];

const SHEET_FILTERS = [
  { key: 'status', label: 'Статус', multi: true, options: SHEET_STATUSES.map(s => ({ value: s, label: s })) },
];

/** Грузим весь отфильтрованный набор (листов немного) и сортируем/пагинируем на клиенте. */
async function fetchSheets(query) {
  const p = { limit: 500, offset: 0 };
  if (query.q) p.q = query.q;
  if (query.hideClosed) p.is_actual = true;   // «кроме закрытых» → только актуальные
  const res = await api.expertise.sheets(p);
  let items = filterByStatus(res.items, query.filters.status);
  items = sortSheets(items, query.sort);
  const total = items.length;
  return { total, items: items.slice(query.offset, query.offset + query.limit) };
}

function renderList() {
  _sheet = null;
  const body = _root.querySelector('.sheets-body');
  body.innerHTML = `
    <div class="sheets-list">
      <div class="sheets-table"></div>
    </div>`;
  const tableEl = body.querySelector('.sheets-table');
  _table = createDataTable(tableEl, {
    storageKey: 'ccpm_dt_sheets_v2',
    columns: SHEET_COLUMNS,
    filters: SHEET_FILTERS,
    closedToggle: { label: 'кроме закрытых' },
    actions: canDo('expertise.write'),
    search: { placeholder: 'Наименование, № проекта/ИП/договора, ГИП, автор' },
    pageSizes: [50, 100, 200],
    defaultSort: 'short_name',
    fetch: fetchSheets,
    // Как «Аукционы»: одинарный клик по строке открывает карточку ИЛ в правой панели.
    onRowClick: (s) => openSheetPanel(s.id),
    emptyHint: canDo('expertise.write')
      ? 'Листов пока нет. «Создать лист» вручную или дождитесь переноса из 1С.'
      : 'Исполнительных листов пока нет.',
  });
  if (canDo('expertise.write')) {
    _table.setActions('<button type="button" class="dt-btn" data-sheets-create>Создать лист</button>');
    tableEl.addEventListener('click', (e) => {
      if (e.target.closest?.('[data-sheets-create]')) { e.preventDefault(); openSheetCreatePanel(); }
    });
  }
}

// ── Карточка листа в правой панели (УИ-2d) ───────────────────────────────────────

/** Сбросить ?id из url при закрытии панели, НЕ трогая переход по ссылке на другой раздел. */
function clearSheetDeepLink() {
  if (location.pathname.endsWith('/projects/sheets') && location.search) {
    history.replaceState(null, '', '/projects/sheets');
  }
}

/** Открыть существующий лист в правой панели (двойной клик / «Открыть карточку» / deep-link). */
async function openSheetPanel(sheetId) {
  let sheet;
  try { sheet = await api.expertise.sheet(sheetId); }
  catch (e) {
    if (e.message !== '401') showNotification(apiErrorText(e), 'error');
    return;
  }
  _sheet = sheet;
  _creating = false;
  _activeTab = 'composition';
  _activeNav = 'card';
  history.replaceState(null, '', `/projects/sheets?id=${sheetId}`);
  _table?.setSelected(sheetId);
  openRecordPanel(sheet, {
    title: () => sheetTitle(_sheet),
    readonly: true,              // футер панели = «Закрыть»; правка — внутри тела карточки
    onClose: () => { _table?.setSelected(null); clearSheetDeepLink(); },
    render: (rec, ctx) => { _panelCtx = ctx; renderSheetCardInto(ctx.host, false); },
  });
}

/** «Создать лист» → панель в режиме создания: пустая шапка-форма на пункте «Исполнительный лист». */
function openSheetCreatePanel() {
  _sheet = {};
  _creating = true;
  _activeTab = 'composition';
  _activeNav = 'card';
  _table?.setSelected(null);
  openRecordPanel(null, {
    title: () => 'Новый исполнительный лист',
    readonly: true,
    onClose: () => { _table?.setSelected(null); clearSheetDeepLink(); },
    render: (rec, ctx) => { _panelCtx = ctx; renderSheetCardInto(ctx.host, true); },
  });
}

/** Тело панели: левое меню «как в 1С» + область контента. host = .rp-pane. */
function renderSheetCardInto(host, create) {
  host.classList.add('sheet-pane');
  host.innerHTML = `
    <div class="sheet-card-main">
      <nav class="sheet-menu" data-sheet-menu>
        ${CARD_NAV.map(n => sheetNavItemHtml(n, create)).join('')}
      </nav>
      <div class="sheet-card-content" data-sheet-content></div>
    </div>`;
  host.querySelector('[data-sheet-menu]').addEventListener('click', (e) => {
    const btn = e.target.closest?.('[data-sheet-nav]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    setCardNav(btn.dataset.sheetNav);
  });
  // Ссылки на другие разделы (Экспертиза/Нормоконтроль) закрывают панель — дальше ведёт роутер.
  host.addEventListener('click', (e) => {
    if (e.target.closest?.('[data-route]')) closeRecordPanel();
  });
  setCardNav(create ? 'card' : _activeNav);
}

/** Пункт левого меню. В режиме создания всё, кроме «Исполнительного листа», заблокировано. */
function sheetNavItemHtml(n, create) {
  const locked = create && n.key !== 'card';
  const active = n.key === _activeNav && !create ? ' is-active' : (n.key === 'card' && create ? ' is-active' : '');
  const stub = n.stub || locked ? ' is-stub' : '';
  const badge = n.stub ? ' <span class="sheet-soon">будет</span>'
    : (locked ? ' <span class="sheet-soon">после сохранения</span>' : '');
  return `<button type="button" class="sheet-menu-item${active}${stub}" data-sheet-nav="${n.key}"${locked ? ' disabled' : ''}>${esc(n.label)}${badge}</button>`;
}

function setCardNav(nav) {
  _activeNav = nav;
  _root.querySelectorAll('[data-sheet-nav]').forEach(b =>
    b.classList.toggle('is-active', b.dataset.sheetNav === nav));
  const content = _root.querySelector('[data-sheet-content]');
  content.innerHTML = '';
  const item = CARD_NAV.find(n => n.key === nav);
  if (item?.stub) { content.innerHTML = stubHtml(item.label); return; }
  if (nav === 'card') renderSheetDoc(content);
  else if (nav === 'changes') renderChanges(content);
  else if (nav === 'letters-in') renderLetters(content);
  else if (nav === 'remarks') renderSheetRemarksNav(content);
  else if (nav === 'normcheck') renderNormcheck(content);
  else if (nav === 'passport') renderPassport(content, { sheetId: _sheet.id });
}

// ── Пункт «Нормоконтроль»: проверки разделов этого листа (НК-3a1) ────────────────

function renderNormcheck(content) {
  content.innerHTML = `
    <section class="dash-widget sheet-normcheck">
      <header class="dash-widget-header"><h3>Нормоконтроль раздела</h3>
        <a class="dt-link" href="/projects/norm-control" data-route="/projects/norm-control">раздел «Нормоконтроль»</a>
      </header>
      <p class="ti-hint">Проверки разделов этого исполнительного листа. «Загрузить на проверку» — мастер с готовым ИЛ.</p>
      <div class="sheet-normcheck-table" data-normcheck></div>
    </section>`;
  createChecksTable(content.querySelector('[data-normcheck]'), {
    sheetId: _sheet.id,
    sheetLabel: sheetTitle(_sheet),
  });
}

function stubHtml(label) {
  return `<section class="dash-widget sheet-stub">
    <header class="dash-widget-header"><h3>${esc(label)}</h3></header>
    <p class="ti-hint">Раздел «${esc(label)}» карточки исполнительного листа — будет.</p>
  </section>`;
}

// ── Пункт «Исполнительный лист»: шапка (форма) + вкладки ────────────────────────

function renderSheetDoc(content) {
  const writer = canDo('expertise.write');
  const create = _creating;
  const s = _sheet;
  content.innerHTML = `
    <section class="dash-widget sheet-head">
      <header class="dash-widget-header"><h3>Реквизиты листа</h3>
        ${create ? '' : `<span class="sheet-head-actual">Актуальность: ${actualLabel(s.is_actual)}</span>`}
      </header>
      <form class="record-panel-form modal-grid" data-sheet-form novalidate>
        ${field('text', 'ip_no', 'Номер ИП', s.ip_no, writer)}
        ${field('datalist', 'status', 'Статус', create ? DEFAULT_STATUS : s.status, writer, SHEET_STATUSES)}
        ${field('text', 'contract_no', '№ договора', s.contract_no, writer)}
        ${field('text', 'business_direction', 'Направление бизнеса', s.business_direction, writer)}
        ${field('number', 'copies', 'Кол-во экземпляров', s.copies, writer)}
        ${counterpartyField(s, writer, create)}
        ${field('text', 'project_no', 'Номер проекта', s.project_no, writer)}
        ${field('datalist', 'pd_stage', 'Стадия ПД', s.pd_stage, writer, PD_STAGES)}
        ${field('datalist', 'work_kind', 'Вид работ', s.work_kind, writer, WORK_KINDS)}
        ${field('text', 'gip', 'ГИП', s.gip, writer)}
        ${field('text', 'author', 'Автор', s.author, writer)}
        ${check('invest_project', 'Инвест-проект', s.invest_project, writer)}
        ${check('assign_code', 'Присваивать шифр', s.assign_code, writer)}
        ${field('wide', 'full_name', 'Наименование полное', s.full_name, writer)}
        ${field('wide', 'short_name', 'Наименование сокр.', s.short_name, writer)}
        ${field('area', 'delivery_order', 'Порядок сдачи', s.delivery_order, writer)}
        ${field('text', 'deal', 'Сделка', s.deal, writer)}
        ${field('text', 'act', 'Акт', s.act, writer)}
        ${field('area', 'approval_comment', 'Комментарий согласования', s.approval_comment, writer)}
        ${create ? '' : `<div class="sheet-form-ccpm">
          <span>Проект CCPM: ${s.project_name ? esc(s.project_name) : '—'}</span>
          ${writer ? '<button type="button" class="dt-link" data-sheet-link>связать…</button>' : ''}
        </div>`}
        ${writer ? `<div class="sheet-form-actions"><button type="submit" class="ti-btn ti-btn-primary" data-sheet-save>${create ? 'Создать' : 'Сохранить'}</button></div>` : ''}
      </form>
    </section>
    ${create ? '' : `<section class="dash-widget sheet-tabs-widget">
      <nav class="sheet-tabs" data-sheet-tabs>
        ${CARD_TABS.map(t => `<button type="button" class="sheet-tab${t.key === _activeTab ? ' is-active' : ''}" data-sheet-tab="${t.key}">${esc(t.label)}</button>`).join('')}
      </nav>
      <div class="sheet-tab-panel" data-sheet-tab-panel></div>
    </section>`}`;

  if (writer) {
    const form = content.querySelector('[data-sheet-form]');
    if (create) form.elements.short_name.required = true;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      if (create) createSheetFromForm(e.currentTarget);
      else saveSheet(e.currentTarget);
    });
    content.querySelector('[data-sheet-link]')?.addEventListener('click', () => openSheetLink(_sheet));
    form.querySelector('[data-cp-pick]')?.addEventListener('click', async () => {
      const res = await pickCounterparty({
        role: 'customer', title: 'Заказчик исполнительного листа',
        currentId: Number(form.elements.counterparty_id.value) || null,
      });
      if (!res) return;
      if (res.action === 'unlink') applyCpLink(form, null, null);
      else if (res.action === 'select') applyCpLink(form, res.id, res.name);
    });
    form.querySelector('[data-cp-unlink]')?.addEventListener('click', () => applyCpLink(form, null, null));
  }
  if (create) return;
  content.querySelector('[data-sheet-tabs]').addEventListener('click', (e) => {
    const btn = e.target.closest?.('[data-sheet-tab]');
    if (btn) { e.preventDefault(); setTab(btn.dataset.sheetTab); }
  });
  setTab(_activeTab);
}

/** Поле формы шапки на общих стилях форм (styles/forms.css: .modal-field + .modal-field-label).
    kind: text | number | datalist | wide | area. */
function field(kind, name, label, value, writer, list) {
  const dis = writer ? '' : ' disabled';
  if (kind === 'area') {
    return `<label class="modal-field modal-field-wide"><span class="modal-field-label">${esc(label)}</span>
      <textarea name="${name}" rows="3" class="modal-textarea"${dis}>${esc(value ?? '')}</textarea></label>`;
  }
  const wide = kind === 'wide' ? ' modal-field-wide' : '';
  const type = kind === 'number' ? 'number' : 'text';
  const listAttr = kind === 'datalist' ? ` list="sheet-dl-${name}"` : '';
  const dl = kind === 'datalist'
    ? `<datalist id="sheet-dl-${name}">${(list ?? []).map(o => `<option value="${esc(o)}"></option>`).join('')}</datalist>`
    : '';
  const val = value == null ? '' : value;
  return `<label class="modal-field${wide}"><span class="modal-field-label">${esc(label)}</span>
    <input type="${type}" name="${name}" value="${esc(val)}"${listAttr}${dis}>${dl}</label>`;
}

// Чекбокс на всю ширину сетки (.cp-check, как в карточках контрагентов — forms.css).
function check(name, label, checked, writer) {
  const dis = writer ? '' : ' disabled';
  return `<label class="cp-check"><input type="checkbox" name="${name}"${checked ? ' checked' : ''}${dis}> ${esc(label)}</label>`;
}

/** Поле «Заказчик»: текст (совместимость) + связь со справочником контрагентов (СПР-1b). */
function cpLinkStatus(s) {
  if (s.counterparty_id) {
    return `<span class="ref-link-status is-linked" data-cp-status>✓ в справочнике: ${esc(s.counterparty_name || s.counterparty || '')}</span>`;
  }
  if ((s.counterparty ?? '').trim()) {
    return '<span class="ref-link-status is-unlinked" data-cp-status>не связан со справочником</span>';
  }
  return '<span class="ref-link-status" data-cp-status></span>';
}

function counterpartyField(s, writer, create = false) {
  const dis = writer ? '' : ' disabled';
  // Связь со справочником (из справочника…/отвязать) работает только у сохранённого листа
  // (applyCpLink шлёт patchSheet по id) — в режиме создания оставляем простой текст.
  const refLink = create ? '' : `<span class="ref-link">${cpLinkStatus(s)}
      ${writer ? '<button type="button" class="dt-link" data-cp-pick>из справочника…</button>' : ''}
      ${writer ? `<button type="button" class="dt-link" data-cp-unlink${s.counterparty_id ? '' : ' hidden'}>отвязать</button>` : ''}
    </span>`;
  return `<label class="modal-field"><span class="modal-field-label">Заказчик</span>
    <input type="text" name="counterparty" value="${esc(s.counterparty ?? '')}"${dis}>
    <input type="hidden" name="counterparty_id" value="${s.counterparty_id ?? ''}">
    ${refLink}</label>`;
}

/** Обновить DOM поля-связки и сохранить только связь (не трогая прочие правки шапки). */
async function applyCpLink(form, id, name) {
  form.elements.counterparty_id.value = id ?? '';
  if (id && name) form.elements.counterparty.value = name;
  const status = form.querySelector('[data-cp-status]');
  const unlink = form.querySelector('[data-cp-unlink]');
  const text = (form.elements.counterparty.value ?? '').trim();
  if (id) {
    status.textContent = `✓ в справочнике: ${name ?? text}`;
    status.className = 'ref-link-status is-linked';
    if (unlink) unlink.hidden = false;
  } else {
    status.textContent = text ? 'не связан со справочником' : '';
    status.className = 'ref-link-status' + (text ? ' is-unlinked' : '');
    if (unlink) unlink.hidden = true;
  }
  try {
    const payload = id ? { counterparty_id: id, counterparty: name } : { counterparty_id: null };
    _sheet = await api.expertise.patchSheet(_sheet.id, payload);
    showNotification(id ? 'Заказчик связан со справочником' : 'Связь со справочником убрана', 'info');
  } catch (e) {
    if (e.message !== '401') showNotification(apiErrorText(e), 'error');
  }
}

/** Собрать тело шапки листа из формы (общее для сохранения и создания). */
function collectHeaderPayload(form) {
  const val = (n) => (form.elements[n]?.value ?? '').trim();
  const copies = val('copies');
  const cpId = form.elements.counterparty_id?.value;
  return {
    ip_no: val('ip_no') || null,
    status: val('status') || null,
    contract_no: val('contract_no') || null,
    business_direction: val('business_direction') || null,
    copies: copies === '' ? null : Number(copies),
    counterparty: val('counterparty') || null,
    counterparty_id: cpId ? Number(cpId) : null,
    project_no: val('project_no') || null,
    pd_stage: val('pd_stage') || null,
    work_kind: val('work_kind') || null,
    gip: val('gip') || null,
    author: val('author') || null,
    invest_project: form.elements.invest_project.checked,
    assign_code: form.elements.assign_code.checked,
    full_name: val('full_name') || null,
    short_name: val('short_name') || null,
    delivery_order: val('delivery_order') || null,
    deal: val('deal') || null,
    act: val('act') || null,
    approval_comment: val('approval_comment') || null,
  };
}

/** Обновить заголовок панели (№ ИП) — после правки шапки, без полной перерисовки. */
function setPanelTitle() {
  const title = _root?.querySelector('.record-panel .ti-title');
  if (title) title.textContent = sheetTitle(_sheet);
}

async function saveSheet(form) {
  const btn = form.querySelector('[data-sheet-save]');
  if (btn) btn.disabled = true;
  try {
    _sheet = await api.expertise.patchSheet(_sheet.id, collectHeaderPayload(form));
    showNotification('Лист сохранён', 'info');
    _panelCtx?.setRecord(_sheet);
    setPanelTitle();
    renderSheetDoc(_root.querySelector('[data-sheet-content]'));
  } catch (e) {
    if (btn) btn.disabled = false;
    if (e.message !== '401') showNotification(apiErrorText(e), 'error');
  }
}

/** Создать лист из формы режима создания → открыть созданный ИЛ в панели (с ?id). */
async function createSheetFromForm(form) {
  if (!form.reportValidity()) return;   // short_name обязателен (native required)
  const btn = form.querySelector('[data-sheet-save]');
  if (btn) btn.disabled = true;
  try {
    const sheet = await api.expertise.createSheet(collectHeaderPayload(form));
    showNotification('Лист создан', 'info');
    _table?.reload();
    openSheetPanel(sheet.id);   // панель переоткрывается в режиме правки (полная карточка)
  } catch (e) {
    if (btn) btn.disabled = false;
    if (e.message !== '401') showNotification(apiErrorText(e), 'error');
  }
}

// ── Вкладки документа ──────────────────────────────────────────────────────────

function setTab(tab) {
  _activeTab = tab;
  _root.querySelectorAll('[data-sheet-tab]').forEach(b =>
    b.classList.toggle('is-active', b.dataset.sheetTab === tab));
  const panel = _root.querySelector('[data-sheet-tab-panel]');
  if (!panel) return;
  const def = CARD_TABS.find(t => t.key === tab);
  if (def?.stub) { panel.innerHTML = `<p class="ti-hint">Вкладка «${esc(def.label)}» — будет.</p>`; return; }
  if (tab === 'composition') renderComposition(panel);
  else if (tab === 'plan') renderPlan(panel);
  else if (tab === 'contacts') renderContacts(panel);
}

/** Общий рендер вкладки-таблицы с CRUD. extra — доп. кнопка { label, onClick }. */
async function renderChildTab(panel, { load, head, rowHtml, addLabel, onAdd, empty, extra }) {
  panel.innerHTML = '<div class="loading-spinner">Загрузка…</div>';
  const writer = canDo('expertise.write');
  let rows;
  try { rows = await load(); }
  catch (e) {
    if (e.message === '401') return;
    panel.innerHTML = `<p class="tenders-error">${esc(apiErrorText(e))}</p>`;
    return;
  }
  panel.innerHTML = `
    ${rows.length ? `<table class="sheet-subtable"><thead><tr>${head}${writer ? '<th></th>' : ''}</tr></thead>
      <tbody>${rows.map((r, i) => rowHtml(r, i, writer)).join('')}</tbody></table>` : `<p class="ti-hint">${esc(empty)}</p>`}
    ${writer ? `<div class="sheet-subtable-actions">
      <button type="button" class="dt-btn" data-child-add>${esc(addLabel)}</button>
      ${extra ? `<button type="button" class="dt-btn" data-child-extra>${esc(extra.label)}</button>` : ''}
    </div>` : ''}`;
  if (!writer) return;
  panel.querySelector('[data-child-add]').addEventListener('click', () => onAdd(null));
  panel.querySelector('[data-child-extra]')?.addEventListener('click', () => extra.onClick());
  panel.querySelectorAll('[data-child-edit]').forEach(b =>
    b.addEventListener('click', () => onAdd(rows[Number(b.dataset.childEdit)])));
}

// — Состав проекта —
function renderComposition(panel) {
  renderChildTab(panel, {
    load: () => api.expertise.sheetComposition(_sheet.id),
    head: '<th>Наименование</th><th>Шифр раздела</th><th class="num">Стоимость</th>',
    rowHtml: (r, i, writer) => `<tr>
      <td>${clip(r.name)}</td><td>${esc(r.section_code ?? '—')}</td><td class="num">${esc(fmtMoney(r.cost))}</td>
      ${writer ? `<td class="sheet-row-actions"><button type="button" class="dt-link" data-child-edit="${i}">править</button></td>` : ''}
    </tr>`,
    addLabel: 'Добавить раздел',
    empty: 'Состав проекта пуст.',
    onAdd: editComposition,
  });
}

async function editComposition(r) {
  const v = await openModal({
    title: r ? 'Раздел состава проекта' : 'Новый раздел состава проекта',
    fields: [
      { name: 'name', label: 'Наименование', value: r?.name ?? '' },
      { name: 'section_code', label: 'Шифр раздела', value: r?.section_code ?? '' },
      { name: 'cost', label: 'Стоимость', type: 'number', step: '0.01', value: r?.cost ?? '' },
      ...(r ? [{ name: '_delete', label: 'Удалить', type: 'select', value: '0',
        options: [{ value: '0', label: 'Нет' }, { value: '1', label: 'Да, удалить' }] }] : []),
    ],
    confirmLabel: r ? 'Сохранить' : 'Добавить',
  });
  if (!v) return;
  await childSubmit({
    row: r, deleted: v._delete === '1',
    body: { name: v.name, section_code: v.section_code, cost: v.cost },
    create: (b) => api.expertise.createSheetComposition(_sheet.id, b),
    patch: (id, b) => api.expertise.patchSheetComposition(id, b),
    del: (id) => api.expertise.deleteSheetComposition(id),
    reopen: () => setTab('composition'),
  });
}

// — Календарный план по договору —
function renderPlan(panel) {
  renderChildTab(panel, {
    load: () => api.expertise.sheetPlan(_sheet.id),
    head: '<th class="num">№</th><th>Наименование</th><th>Начало</th><th>Окончание</th><th>Выполнено</th>',
    rowHtml: (r, i, writer) => `<tr>
      <td class="num">${esc(r.n ?? '—')}</td><td>${clip(r.name)}</td>
      <td>${fmtDate(r.start_date)}</td><td>${fmtDate(r.end_date)}</td><td>${yesNo(r.done)}</td>
      ${writer ? `<td class="sheet-row-actions"><button type="button" class="dt-link" data-child-edit="${i}">править</button></td>` : ''}
    </tr>`,
    addLabel: 'Добавить строку',
    empty: 'Календарный план пуст.',
    onAdd: editPlan,
  });
}

async function editPlan(r) {
  const v = await openModal({
    title: r ? 'Строка календарного плана' : 'Новая строка плана',
    fields: [
      { name: 'n', label: '№', type: 'number', value: r?.n ?? '' },
      { name: 'name', label: 'Наименование', value: r?.name ?? '' },
      { name: 'start_date', label: 'Начало', type: 'date', value: fmtDateInput(r?.start_date) },
      { name: 'end_date', label: 'Окончание', type: 'date', value: fmtDateInput(r?.end_date) },
      { name: 'done', label: 'Выполнено', type: 'select', value: r?.done ? '1' : '0',
        options: [{ value: '0', label: 'Нет' }, { value: '1', label: 'Да' }] },
      ...(r ? [{ name: '_delete', label: 'Удалить', type: 'select', value: '0',
        options: [{ value: '0', label: 'Нет' }, { value: '1', label: 'Да, удалить' }] }] : []),
    ],
    confirmLabel: r ? 'Сохранить' : 'Добавить',
  });
  if (!v) return;
  await childSubmit({
    row: r, deleted: v._delete === '1',
    body: { n: v.n == null ? null : Number(v.n), name: v.name,
      start_date: v.start_date, end_date: v.end_date, done: v.done === '1' },
    create: (b) => api.expertise.createSheetPlan(_sheet.id, b),
    patch: (id, b) => api.expertise.patchSheetPlan(id, b),
    del: (id) => api.expertise.deleteSheetPlan(id),
    reopen: () => setTab('plan'),
  });
}

// — Контактное лицо —
function renderContacts(panel) {
  renderChildTab(panel, {
    load: () => api.expertise.sheetContacts(_sheet.id),
    head: '<th>ФИО</th><th>Должность</th><th>Email</th><th>Телефон</th><th>Справочник</th>',
    rowHtml: (r, i, writer) => `<tr>
      <td>${clip(r.fio)}</td><td>${clip(r.position)}</td><td>${clip(r.email)}</td><td>${esc(r.phone ?? '—')}</td>
      <td>${r.person_id ? '✓ в «Людях»' : '—'}</td>
      ${writer ? `<td class="sheet-row-actions"><button type="button" class="dt-link" data-child-edit="${i}">править</button></td>` : ''}
    </tr>`,
    addLabel: 'Добавить контакт',
    empty: 'Контактных лиц нет.',
    onAdd: editContact,
    extra: canDo('expertise.write') ? { label: 'Выбрать из «Людей»', onClick: addContactFromPerson } : null,
  });
}

/** Добавить контакт, выбрав человека из справочника (или создав нового). */
async function addContactFromPerson() {
  const res = await pickPerson({
    title: 'Контактное лицо из справочника «Люди»',
    role: 'contact',
    counterpartyId: _sheet.counterparty_id || null,
  });
  if (!res || res.action !== 'select') return;
  try {
    const p = await api.refs.person(res.id);
    await api.expertise.createSheetContact(_sheet.id, {
      fio: p.fio, position: p.position || null, email: p.email || null,
      phone: p.phone || null, person_id: p.id,
    });
    showNotification('Контакт добавлен из справочника', 'info');
    setTab('contacts');
  } catch (e) {
    if (e.message !== '401') showNotification(apiErrorText(e), 'error');
  }
}

async function editContact(r) {
  const v = await openModal({
    title: r ? 'Контактное лицо' : 'Новое контактное лицо',
    fields: [
      { name: 'fio', label: 'ФИО', value: r?.fio ?? '' },
      { name: 'position', label: 'Должность', value: r?.position ?? '' },
      { name: 'email', label: 'Email', value: r?.email ?? '' },
      { name: 'phone', label: 'Телефон', value: r?.phone ?? '' },
      ...(r ? [{ name: '_delete', label: 'Удалить', type: 'select', value: '0',
        options: [{ value: '0', label: 'Нет' }, { value: '1', label: 'Да, удалить' }] }] : []),
    ],
    confirmLabel: r ? 'Сохранить' : 'Добавить',
  });
  if (!v) return;
  await childSubmit({
    row: r, deleted: v._delete === '1',
    body: { fio: v.fio, position: v.position, email: v.email, phone: v.phone },
    create: (b) => api.expertise.createSheetContact(_sheet.id, b),
    patch: (id, b) => api.expertise.patchSheetContact(id, b),
    del: (id) => api.expertise.deleteSheetContact(id),
    reopen: () => setTab('contacts'),
  });
}

/** Общая отправка строки вкладки (создать / сохранить / удалить) с уведомлением. */
async function childSubmit({ row, deleted, body, create, patch, del, reopen }) {
  try {
    if (row && deleted) {
      const ok = await openModal({
        title: 'Удалить строку?', message: 'Строка будет удалена без возможности восстановления.',
        confirmLabel: 'Удалить', danger: true,
      });
      if (!ok) return;
      await del(row.id);
      showNotification('Строка удалена', 'info');
    } else if (row) {
      await patch(row.id, body);
      showNotification('Сохранено', 'info');
    } else {
      await create(body);
      showNotification('Добавлено', 'info');
    }
    reopen();
  } catch (e) {
    if (e.message !== '401') showNotification(apiErrorText(e), 'error');
  }
}

// ── Пункт «Изменения»: разрешения на изменения этого листа (ЭКС-1b) ─────────────

async function renderChanges(content) {
  content.innerHTML = '<div class="loading-spinner">Загрузка…</div>';
  if (!_sheet.short_name) {
    content.innerHTML = `<section class="dash-widget"><header class="dash-widget-header"><h3>Изменения</h3></header>
      <p class="ti-hint">У листа нет наименования сокр. — разрешения сопоставляются по наименованию. Заполните его в реквизитах.</p></section>`;
    return;
  }
  let data;
  try { data = await api.expertise.permits({ sheet: _sheet.short_name, limit: 200, sort: '-permit_date' }); }
  catch (e) {
    if (e.message === '401') return;
    content.innerHTML = `<p class="tenders-error">${esc(apiErrorText(e))}</p>`;
    return;
  }
  const rows = data.items.map(p => `<tr>
      <td>${esc(p.permit_no ?? '—')}</td><td>${fmtDate(p.permit_date)}</td>
      <td>${clip(p.essence)}</td>
      <td>${esc(CHANGE_CODE_LABEL[p.change_code] ?? (p.change_code_raw || '—'))}</td>
      <td>${clip(p.gip_user_name || p.gip)}</td><td>${clip(p.author_user_name || p.author)}</td>
    </tr>`).join('');
  content.innerHTML = `
    <section class="dash-widget">
      <header class="dash-widget-header"><h3>Разрешения на изменения</h3>
        <a class="dt-link" href="/projects/expertise" data-route="/projects/expertise">раздел «Экспертиза»</a>
      </header>
      ${data.items.length ? `<table class="sheet-subtable"><thead><tr>
        <th>№</th><th>Дата</th><th>Суть изменений</th><th>Код</th><th>ГИП</th><th>Автор</th>
      </tr></thead><tbody>${rows}</tbody></table>` : '<p class="ti-hint">Разрешений по этому листу нет.</p>'}
      <p class="ti-hint">Создание и правка разрешений — в разделе
        <a class="dt-link" href="/projects/expertise" data-route="/projects/expertise">«Экспертиза»</a>.</p>
    </section>`;
}

// ── Пункт «Замечания»: замечания листа ──────────────────────────────────────────

function renderSheetRemarksNav(content) {
  content.innerHTML = `
    <section class="dash-widget sheet-remarks">
      <header class="dash-widget-header"><h3>Замечания листа</h3>
        <label class="sheet-drafts-toggle"><input type="checkbox" data-remarks-drafts> показать черновики</label>
      </header>
      <div class="sheet-remarks-list" data-remarks><div class="loading-spinner">Загрузка…</div></div>
    </section>`;
  content.querySelector('[data-remarks-drafts]').addEventListener('change', (e) => {
    loadSheetRemarks(content, e.target.checked);
  });
  loadSheetRemarks(content, false);
}

async function loadSheetRemarks(scope, drafts) {
  const el = scope.querySelector('[data-remarks]');
  el.innerHTML = '<div class="loading-spinner">Загрузка…</div>';
  let data;
  try {
    data = await api.expertise.remarks({
      sheet: _sheet.short_name || undefined,
      drafts: drafts ? 1 : 0, limit: 200, sort: '-remark_date',
    });
  } catch (e) {
    if (e.message === '401') return;
    el.innerHTML = `<p class="tenders-error">${esc(apiErrorText(e))}</p>`;
    return;
  }
  if (!data.items.length) {
    el.innerHTML = `<p class="ti-hint">Замечаний ${drafts ? '' : '(подтверждённых) '}нет.
      <a class="dt-link" href="/projects/expertise" data-route="/projects/expertise">открыть раздел «Экспертиза»</a></p>`;
    return;
  }
  el.innerHTML = `<table class="sheet-subtable"><thead><tr>
      <th>№</th><th>Раздел</th><th>Замечание</th><th>Ответ</th><th>Снято</th>${drafts ? '<th>Статус</th>' : ''}
    </tr></thead><tbody>${data.items.map(r => `<tr>
      <td>${esc(r.remark_no ?? '—')}</td><td>${clip(r.section_name || r.section_raw)}</td>
      <td>${clip(r.remark_text)}</td><td>${clip(r.answer_text)}</td><td>${r.is_closed ? 'Да' : '—'}</td>
      ${drafts ? `<td>${r.confirmed ? 'в базе' : 'черновик'}</td>` : ''}
    </tr>`).join('')}</tbody></table>`;
}

// ── Пункт «Входящие письма»: письма экспертизы (загрузка и разбор, ЭКС-1b-1) ─────

function renderLetters(content) {
  const writer = canDo('expertise.write');
  content.innerHTML = `
    <section class="dash-widget sheet-files">
      <header class="dash-widget-header"><h3>Письма экспертизы</h3></header>
      ${writer ? uploadFormHtml() : '<p class="ti-hint">Загрузка писем — у роли с правом на изменения.</p>'}
      <div class="sheet-files-list" data-files><div class="loading-spinner">Загрузка…</div></div>
      <div class="sheet-drafts" data-drafts></div>
    </section>`;
  if (writer) bindUploadForm(content);
  loadFiles(content);
}

function uploadFormHtml() {
  return `
    <form class="sheet-upload" data-upload>
      <input type="file" name="file" required class="tenders-file" data-upload-file
             accept=".doc,.docx,.xls,.xlsx,.pdf">
      <input type="text" name="expertise_org" placeholder="Организация экспертизы">
      <input type="text" name="letter_no" placeholder="№ письма">
      <input type="date" name="letter_date" title="Дата письма">
      <select name="section_id" data-upload-section title="Раздел по умолчанию">
        <option value="">— раздел определит ИИ —</option>
      </select>
      <button type="submit" class="ti-btn ti-btn-primary" data-upload-submit>Загрузить и разобрать</button>
      <p class="ti-hint">Word (docx) и Excel (xlsx/xls) раскладываются на черновики замечаний.
        PDF и нестандартные файлы разбирает ИИ-раннер — после загрузки они ждут разбора,
        затем появятся черновики замечаний на проверку. Раздел по умолчанию применится
        только к замечаниям, для которых раздел не определит сам парсер/ИИ.</p>
    </form>`;
}

function bindUploadForm(content) {
  const form = content.querySelector('[data-upload]');
  if (!form) return;
  // Наполнить «Раздел по умолчанию» из справочника (плейсхолдер уже в разметке).
  const sectionSel = form.querySelector('[data-upload-section]');
  loadSections().then((secs) => {
    if (!sectionSel) return;
    for (const s of (secs ?? [])) {
      const o = document.createElement('option');
      o.value = String(s.id);
      o.textContent = s.code != null ? `${s.name} (${s.code})` : s.name;
      sectionSel.appendChild(o);
    }
  });
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const file = form.querySelector('[data-upload-file]').files?.[0];
    if (!file) return;
    const btn = form.querySelector('[data-upload-submit]');
    btn.disabled = true;
    try {
      const res = await api.expertise.uploadSheetFile(_sheet.id, file, {
        expertise_org: form.expertise_org.value,
        letter_no: form.letter_no.value,
        letter_date: form.letter_date.value,
        section_id: form.section_id.value || undefined,
      });
      form.reset();
      const f = res.file;
      if (f.parse_status === 'done') showNotification(`Разобрано черновиков: ${res.drafts}`, 'info');
      else if (f.parse_status === 'failed') showNotification(`Файл сохранён, но не разобран: ${f.parse_error ?? ''}`, 'error');
      else showNotification('Файл сохранён, ждёт ИИ-разбора', 'info');
      await loadFiles(content);
      if (res.drafts) openDrafts(content, f.id);
    } catch (err) {
      if (err.message !== '401') showNotification(apiErrorText(err), 'error');
    } finally {
      btn.disabled = false;
    }
  });
}

async function loadFiles(content) {
  const el = content.querySelector('[data-files]');
  if (!el) return;
  let data;
  try { data = await api.expertise.sheetFiles(_sheet.id); }
  catch (e) {
    if (e.message === '401') return;
    el.innerHTML = `<p class="tenders-error">${esc(apiErrorText(e))}</p>`;
    return;
  }
  if (!data.items.length) { el.innerHTML = '<p class="ti-hint">Писем ещё нет.</p>'; return; }
  const writer = canDo('expertise.write');
  el.innerHTML = `<table class="sheet-subtable"><thead><tr>
      <th>Файл</th><th>Организация</th><th>№ письма</th><th>Дата</th><th>Разбор</th><th>Замечаний</th><th></th>
    </tr></thead><tbody>${data.items.map(f => `
      <tr data-sheet-file="${f.id}">
        <td>${clip(f.filename)}</td><td>${clip(f.expertise_org)}</td>
        <td>${esc(f.letter_no ?? '—')}</td><td>${fmtDate(f.letter_date)}</td>
        <td>${esc(PARSE_STATUS[f.parse_status] ?? f.parse_status)}${f.parse_error ? ` <span class="ti-hint" title="${esc(f.parse_error)}">(?)</span>` : ''}</td>
        <td class="num">${f.remark_count}${f.draft_count ? ` <span class="ti-hint">(${f.draft_count} черн.)</span>` : ''}</td>
        <td class="sheet-row-actions">
          <a href="${esc(api.expertise.fileUrl(f.id))}" class="dt-link" download>скачать</a>
          ${writer && f.parse_status !== 'parsing' ? `<button type="button" class="dt-link" data-file-parse="${f.id}">разобрать ИИ сейчас</button>` : ''}
          ${writer && f.draft_count ? `<button type="button" class="dt-link" data-file-check="${f.id}">проверить замечания</button>` : ''}
        </td>
      </tr>`).join('')}</tbody></table>`;
  el.querySelectorAll('[data-file-check]').forEach(btn =>
    btn.addEventListener('click', () => openDrafts(content, Number(btn.dataset.fileCheck))));
  el.querySelectorAll('[data-file-parse]').forEach(btn =>
    btn.addEventListener('click', () => reparseFile(content, Number(btn.dataset.fileParse))));
}

// ЭКС-1b-2: вернуть письмо в очередь ИИ-разбора (кнопка «разобрать ИИ сейчас»).
async function reparseFile(content, fileId) {
  try {
    await api.expertise.parseFile(fileId);
    showNotification('Поставлено в очередь ИИ-разбора — раннер возьмёт письмо в течение нескольких минут', 'info');
    await loadFiles(content);
  } catch (e) {
    if (e.message !== '401') showNotification(apiErrorText(e), 'error');
  }
}

// ── Проверка черновиков файла ─────────────────────────────────────────────────

async function openDrafts(content, fileId) {
  const el = content.querySelector('[data-drafts]');
  el.innerHTML = '<div class="loading-spinner">Загрузка замечаний…</div>';
  await loadSections();
  let data;
  try { data = await api.expertise.fileRemarks(fileId); }
  catch (e) {
    if (e.message === '401') return;
    el.innerHTML = `<p class="tenders-error">${esc(apiErrorText(e))}</p>`;
    return;
  }
  const drafts = data.items.filter(r => !r.confirmed);
  if (!drafts.length) {
    el.innerHTML = '<p class="ti-hint">Неподтверждённых замечаний в этом письме нет.</p>';
    await loadFiles(content);
    return;
  }
  el.innerHTML = `
    <div class="sheet-drafts-panel" data-drafts-panel>
      <h4>Черновики замечаний (${drafts.length}) — проверьте раздел и текст, затем подтвердите</h4>
      <table class="sheet-subtable"><thead><tr>
        <th>№</th><th>Раздел</th><th>Замечание</th><th>Норматив</th><th></th>
      </tr></thead><tbody>${drafts.map(r => draftRowHtml(r)).join('')}</tbody></table>
      <div class="sheet-drafts-actions">
        <button type="button" class="ti-btn ti-btn-primary" data-confirm-all>Подтвердить все (${drafts.length})</button>
        <button type="button" class="dt-btn" data-drafts-close>Закрыть</button>
      </div>
    </div>`;
  const panel = el.querySelector('[data-drafts-panel]');
  panel.querySelectorAll('[data-draft-edit]').forEach(btn =>
    btn.addEventListener('click', () => editDraft(content, fileId, drafts.find(r => r.id === Number(btn.dataset.draftEdit)))));
  panel.querySelectorAll('[data-draft-del]').forEach(btn =>
    btn.addEventListener('click', () => deleteDraft(content, fileId, Number(btn.dataset.draftDel))));
  panel.querySelector('[data-confirm-all]').addEventListener('click', () => confirmDrafts(content, fileId));
  panel.querySelector('[data-drafts-close]').addEventListener('click', () => { el.innerHTML = ''; });
}

function draftRowHtml(r) {
  const section = r.section_name || r.section_raw || '—';
  return `<tr data-draft-row="${r.id}">
    <td>${esc(r.remark_no ?? '—')}</td>
    <td>${clip(section)}${r.section_id ? '' : ' <span class="ti-hint">(не сопоставлен)</span>'}</td>
    <td>${clip(r.remark_text)}</td>
    <td>${clip((r.norm_refs || []).join('; '))}</td>
    <td class="sheet-row-actions">
      <button type="button" class="dt-link" data-draft-edit="${r.id}">править</button>
      <button type="button" class="dt-link" data-draft-del="${r.id}">удалить</button>
    </td>
  </tr>`;
}

function sectionOptions(selectedId) {
  return [{ value: '', label: '— без раздела' }].concat(
    (_sections ?? []).map(s => ({ value: String(s.id), label: s.code != null ? `${s.name} (${s.code})` : s.name })),
  ).map(o => ({ ...o, _sel: String(selectedId ?? '') === o.value }));
}

async function editDraft(content, fileId, r) {
  if (!r) return;
  await loadSections();
  const v = await openModal({
    title: `Замечание ${r.remark_no ?? ''}`.trim(),
    message: r.section_id ? '' : `Раздел из файла: ${r.section_raw ?? '—'}`,
    fields: [
      { name: 'section', label: 'Раздел', type: 'select', value: String(r.section_id ?? ''), options: sectionOptions(r.section_id) },
      { name: 'remark_text', label: 'Текст замечания', type: 'textarea', rows: 4, value: r.remark_text ?? '' },
      { name: 'answer_text', label: 'Ответ', type: 'textarea', rows: 3, value: r.answer_text ?? '' },
    ],
    confirmLabel: 'Сохранить',
  });
  if (!v) return;
  try {
    await api.expertise.patchRemark(r.id, {
      section_id: v.section ? Number(v.section) : null,
      remark_text: v.remark_text,
      answer_text: v.answer_text,
    });
    showNotification('Черновик обновлён', 'info');
    openDrafts(content, fileId);
  } catch (e) {
    if (e.message !== '401') showNotification(apiErrorText(e), 'error');
  }
}

async function deleteDraft(content, fileId, remarkId) {
  const ok = await openModal({
    title: 'Удалить замечание?',
    message: 'Черновик будет удалён без возможности восстановления.',
    confirmLabel: 'Удалить', danger: true,
  });
  if (!ok) return;
  try {
    await api.expertise.deleteRemark(remarkId);
    showNotification('Черновик удалён', 'info');
    openDrafts(content, fileId);
  } catch (e) {
    if (e.message !== '401') showNotification(apiErrorText(e), 'error');
  }
}

async function confirmDrafts(content, fileId) {
  try {
    const res = await api.expertise.confirmFile(fileId, null);
    showNotification(`Подтверждено замечаний: ${res.confirmed}`, 'info');
    content.querySelector('[data-drafts]').innerHTML = '';
    await loadFiles(content);
  } catch (e) {
    if (e.message !== '401') showNotification(apiErrorText(e), 'error');
  }
}

// ── Связать лист с проектом CCPM ────────────────────────────────────────────────

async function openSheetLink(sheet) {
  let suggestions = [];
  try { suggestions = (await api.expertise.sheetSuggestions(sheet.id)).items ?? []; } catch { /* пусто */ }
  const options = [{ value: '', label: sheet.project_id ? '— отвязать от проекта' : '— не связывать' }]
    .concat(suggestions.map(p => ({ value: String(p.id), label: p.project_code ? `${p.name} (${p.project_code})` : p.name })));
  if (sheet.project_id && !suggestions.some(p => p.id === sheet.project_id)) {
    options.push({ value: String(sheet.project_id), label: `текущий проект #${sheet.project_id}` });
  }
  const v = await openModal({
    title: `Связать лист «${sheet.short_name ?? sheet.id}» с проектом CCPM`,
    message: suggestions.length ? 'Подсказки по номеру проекта и наименованию:' : 'Похожих проектов не нашлось — можно указать id вручную.',
    fields: [
      { name: 'project', label: 'Проект', type: 'select', value: String(sheet.project_id ?? ''), options },
      { name: 'manual_id', label: 'Или id проекта вручную', type: 'number', placeholder: 'необязательно' },
    ],
    confirmLabel: 'Сохранить',
  });
  if (!v) return;
  const projectId = v.manual_id != null && v.manual_id !== '' ? Number(v.manual_id) : (v.project ? Number(v.project) : null);
  try {
    _sheet = await api.expertise.patchSheet(sheet.id, { project_id: projectId });
    showNotification(projectId ? 'Лист связан с проектом' : 'Лист отвязан от проекта', 'info');
    renderSheetDoc(_root.querySelector('[data-sheet-content]'));
  } catch (e) {
    if (e.message !== '401') showNotification(apiErrorText(e), 'error');
  }
}
